/**
 * Customer type UI: the directory/list badge, the catalog-failure notice, and
 * the assignment card on the detail.
 *
 * Assignment goes through `assignCustomerType` with BOTH fields — the target
 * and the assignment the operator was looking at (`expected_customer_type_id`).
 * A `CUSTOMER_TYPE_CONFLICT` means someone changed it first: the record is
 * re-read and the operator decides again; nothing is retried with a fresh
 * expectation automatically. Success feedback fires only after a point reread
 * confirms the persisted value — a 200 is a receipt, not a confirmation.
 *
 * Only ACTIVE types are offered for a new assignment. An existing archived (or
 * unresolved) assignment stays visible as the current value and can be cleared
 * or replaced, never silently dropped.
 *
 * The chooser is the design system's `Dropdown`, not a native `<select>`: the
 * drop forbids native selects on this surface, and the archived/unknown current
 * value needs a disabled, explanatory option a native select cannot express.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { Btn, Dropdown, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { assignCustomerType } from "@v0y4ge/api-client";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useT } from "../../lib/i18n";
import { codeOf, isDenied, messageOf, resolveType, type TypeCatalog } from "./customer-types";
import { scopeProbe } from "./customers-data";

const NONE = "__none";

export function TypeBadge({ catalog, id, size = "sm" }: { catalog: TypeCatalog; id: string | null | undefined; size?: "sm" | "md" }) {
  const t = useT();
  const r = resolveType(catalog.byId, id);
  const base: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 5, padding: size === "md" ? "3px 10px" : "2px 8px", borderRadius: 999, fontSize: size === "md" ? 11.5 : 10.5, fontWeight: 600, whiteSpace: "nowrap", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" };
  if (r.kind === "unassigned") return <span style={{ ...base, color: T.soft, border: `1px dashed ${T.n5}` }}>{t("customers.type.unassigned", "Unassigned")}</span>;
  if (r.kind === "unknown") {
    // Names could not be read (loading, denied, failed) or the id is gone. A
    // denied catalog says so explicitly rather than calling the type unknown.
    const label = catalog.loading && !catalog.loaded
      ? t("customers.type.loading", "Type\u2026")
      : catalog.denied
        ? t("customers.type.unavailable", "Type not available")
        : t("customers.type.unknown", "Unknown type");
    return <span title={r.id} style={{ ...base, color: T.orange1, background: T.orange2 }}>{label}</span>;
  }
  if (r.kind === "archived") return <span title={t("customers.type.archivedTitle", "This type is archived. It can be cleared or replaced, not newly assigned.")} style={{ ...base, color: T.n2, background: T.n6, textDecoration: "line-through" }}>{r.name}</span>;
  return <span style={{ ...base, color: T.tDarkBlue, background: `color-mix(in srgb, ${T.tDarkBlue} 10%, ${T.white})` }}>{r.name}</span>;
}

/** Strip under the toolbar when type names could not be read. The list stays
 *  usable: assignments still render as set or unset. */
export function TypeCatalogNotice({ catalog }: { catalog: TypeCatalog }) {
  const t = useT();
  if (catalog.loaded || catalog.loading) return null;
  if (!catalog.error && !catalog.denied) return null;
  return (
    <div role="status" style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderRadius: 6, background: T.orange2, fontSize: 12, color: T.n1 }}>
      <span style={{ display: "inline-flex", color: T.orange1 }}><Icon.warn size={14} /></span>
      <span style={{ flex: 1 }}>
        {catalog.denied
          ? t("customers.type.denied", "Type names aren\u2019t available to you. Assignments still show as set or unset.")
          : `${t("customers.type.loadFailed", "Type names couldn\u2019t be read.")} ${catalog.error ?? ""}`}
      </span>
      {catalog.denied ? null : (
        <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} onClick={() => { void catalog.reload(); }}>{t("common.actions.retry", "Retry")}</Btn>
      )}
    </div>
  );
}

export function CustomerTypeCard({ customerId, currentTypeId, catalog, onRefetch }: {
  customerId: string;
  /** The assignment as LAST READ — also the conflict expectation on save. */
  currentTypeId: string | null;
  catalog: TypeCatalog;
  /** Point reread of THIS record; resolves to the persisted assignment.
   *  `undefined` means the reread could not establish it. */
  onRefetch: () => Promise<string | null | undefined>;
}) {
  const t = useT();
  const canAssign = can("customers.write");
  const current = resolveType(catalog.byId, currentTypeId);
  const [selected, setSelected] = useState<string>(currentTypeId ?? NONE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // A reread (ours, or a scope change) moved the current value: the draft
  // follows it. The conflict explanation stays until the operator acts again,
  // so it is not wiped by the very reread it triggered.
  useEffect(() => { setSelected(currentTypeId ?? NONE); }, [currentTypeId, customerId]);
  useEffect(() => { setConflict(false); setError(null); }, [customerId]);

  const target = selected === NONE ? null : selected;
  const dirty = target !== (currentTypeId ?? null);
  const currentIsSelectable = current.kind === "active";

  const commit = async (next: string | null) => {
    if (saving) return;
    const inScope = scopeProbe();
    if (!inScope()) return;
    setSaving(true); setError(null); setConflict(false);
    try {
      const res = await assignCustomerType(customerId, {
        customer_type_id: next,
        expected_customer_type_id: currentTypeId ?? null,
      });
      if (!inScope()) return;
      // Persistence is confirmed by the reread, not by the 200.
      const persisted = await onRefetch();
      if (!inScope()) return;
      if (persisted === undefined || (persisted ?? null) !== next) {
        setError(t("customers.type.verifyFailed", "The refreshed record does not show the requested type. Check the current value before trying again."));
        return;
      }
      const name = res.customer_type_id ? catalog.byId.get(res.customer_type_id)?.name ?? null : null;
      toast({
        tone: "success",
        title: res.customer_type_id ? t("customers.type.assignedToast", "Type saved") : t("customers.type.clearedToast", "Type cleared"),
        subtitle: name ?? undefined,
      });
    } catch (e) {
      if (!inScope()) return;
      const code = codeOf(e);
      if (code === "CUSTOMER_TYPE_CONFLICT") {
        setConflict(true);
        await onRefetch();
      } else if (code === "CUSTOMER_TYPE_ARCHIVED_CONFLICT") {
        setError(t("customers.type.archivedConflict", "That type was archived while you were choosing. The list has been refreshed \u2014 pick another."));
        await catalog.reload();
      } else if (code === "CUSTOMER_TYPE_NOT_FOUND") {
        setError(t("customers.type.notFound", "That type or record no longer exists. The list has been refreshed."));
        await Promise.all([catalog.reload(), onRefetch()]);
      } else if (isDenied(e)) {
        setError(t("customers.type.forbidden", "You don\u2019t have permission to change the type."));
      } else {
        setError(`${t("customers.type.saveFailed", "The type couldn\u2019t be saved.")} ${messageOf(e)}`);
      }
    } finally { if (inScope()) setSaving(false); }
  };

  const options = [
    { value: NONE, label: t("customers.type.optionNone", "No type") },
    ...catalog.active.map((ct) => ({ value: ct.id, label: ct.name })),
    ...(currentTypeId && !currentIsSelectable
      ? [{
        value: currentTypeId,
        label: current.kind === "archived"
          ? `${current.name} \u2014 ${t("customers.type.archivedSuffix", "archived")}`
          : `${t("customers.type.unknown", "Unknown type")} (${currentTypeId})`,
        disabled: current.kind === "archived",
      }]
      : []),
  ];

  const readOnlyView = !canAssign || (catalog.denied && !catalog.loaded) || (!!catalog.error && !catalog.loaded);

  return (
    <div data-testid="customer-type-card" style={{ display: "grid", gridTemplateColumns: "minmax(140px, 220px) minmax(0, 1fr)", gap: "10px 16px", padding: "14px 16px", alignItems: "start" }}>
      <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 600, paddingTop: 7 }}>{t("customers.type.cardTitle", "Type")}</span>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        {/* One control, one truth. The badge is the READ view; where the type
            can be changed the select already states the current value, and
            showing both stacked said the same thing twice in two shapes. */}
        {readOnlyView ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <TypeBadge catalog={catalog} id={currentTypeId} size="md" />
            {current.kind === "archived" ? <Pill tone="neutral">{t("customers.type.archivedNote", "Assigned type is archived")}</Pill> : null}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ width: 260, maxWidth: "100%" }}>
              <Dropdown<string>
                size="sm"
                /* The card that holds this control is `overflow: hidden` (it
                 * clips its own rounded corners), so an in-flow menu is cut off
                 * at the card edge — the options were rendering behind the next
                 * section. `portal` moves the menu to document.body with fixed
                 * coordinates, which is what this DS control provides for
                 * exactly this case. */
                portal
                aria-label={t("customers.type.cardTitle", "Type")}
                value={selected}
                disabled={saving || catalog.loading}
                options={options}
                onChange={(v) => { setSelected(v); setError(null); }}
              />
            </div>
            <Btn size="sm" disabled={!dirty || saving} onClick={() => { void commit(target); }}>
              {saving ? t("common.actions.saving", "Saving\u2026") : t("common.actions.save", "Save")}
            </Btn>
            {currentTypeId ? (
              <Btn variant="ghost" size="sm" disabled={saving} onClick={() => { void commit(null); }}>{t("customers.type.clear", "Clear type")}</Btn>
            ) : null}
            {current.kind === "archived" ? <Pill tone="neutral">{t("customers.type.archivedNote", "Assigned type is archived")}</Pill> : null}
          </div>
        )}

        {conflict ? (
          <div role="alert" style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 10px", borderRadius: 6, background: T.orange2, maxWidth: 520 }}>
            <span style={{ display: "inline-flex", marginTop: 1, color: T.orange1 }}><Icon.warn size={13} /></span>
            <span style={{ fontSize: 12, color: T.n1, lineHeight: 1.5 }}>{t("customers.type.conflict", "The type changed elsewhere before your save. The current value is shown \u2014 nothing was overwritten.")}</span>
          </div>
        ) : null}

        {catalog.denied && !catalog.loaded ? (
          <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>{t("customers.type.choicesDenied", "Type names aren\u2019t available to you, so a new type can\u2019t be chosen here. Clearing is still possible.")}</span>
        ) : catalog.error && !catalog.loaded ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: T.n1 }}>
            <span>{t("customers.type.loadFailed", "Type names couldn\u2019t be read.")}</span>
            <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} onClick={() => { void catalog.reload(); }}>{t("common.actions.retry", "Retry")}</Btn>
          </div>
        ) : null}

        {error ? <span role="alert" style={{ fontSize: 12, color: T.red1, lineHeight: 1.5 }}>{error}</span> : null}
        {!canAssign ? <span style={{ fontSize: 11, color: T.soft }}>{t("customers.type.readOnly", "Changing the type needs the write permission.")}</span> : null}
      </div>
    </div>
  );
}
